export type LosEndpoint = {
  lng: number;
  lat: number;
  /** Высота над землёй (AGL), м */
  aglM: number;
};

export type LosSample = {
  lng: number;
  lat: number;
  t: number;
  groundM: number;
  rayM: number;
  blocked: boolean;
};

export type LosResult = {
  visible: boolean;
  samples: LosSample[];
  blockAt: { lng: number; lat: number } | null;
  observerElevM: number;
  targetElevM: number;
  usedTerrain: boolean;
};

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * Прямая видимость: луч от (ground+agl) observer → target.
 * blocked, если ground сэмпла выше луча (с запасом eps).
 */
export function computeLos(
  observer: LosEndpoint,
  target: LosEndpoint,
  sampleGroundM: (lng: number, lat: number) => number | null,
  steps = 64,
  epsM = 0.5,
): LosResult {
  const n = Math.max(8, Math.min(256, steps));
  let usedTerrain = false;
  const groundAt = (lng: number, lat: number): number => {
    const v = sampleGroundM(lng, lat);
    if (v != null && Number.isFinite(v)) {
      usedTerrain = true;
      return v;
    }
    return 0;
  };

  const og = groundAt(observer.lng, observer.lat);
  const tg = groundAt(target.lng, target.lat);
  const observerElevM = og + Math.max(0, observer.aglM);
  const targetElevM = tg + Math.max(0, target.aglM);

  const samples: LosSample[] = [];
  let blockAt: { lng: number; lat: number } | null = null;

  for (let i = 0; i <= n; i++) {
    const t = i / n;
    const lng = lerp(observer.lng, target.lng, t);
    const lat = lerp(observer.lat, target.lat, t);
    const groundM = groundAt(lng, lat);
    const rayM = lerp(observerElevM, targetElevM, t);
    const isEnd = i === 0 || i === n;
    const blocked = !isEnd && groundM > rayM + epsM;
    samples.push({ lng, lat, t, groundM, rayM, blocked });
    if (blocked && blockAt == null) {
      blockAt = { lng, lat };
    }
  }

  return {
    visible: blockAt == null,
    samples,
    blockAt,
    observerElevM,
    targetElevM,
    usedTerrain,
  };
}
